import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { RevealLineScroll, FadeUp } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import HeroVault from "@/components/HeroVault";
import { api, fmtAUD } from "@/lib/api";
import { ArrowUpRight, Lock, ShieldOff, Radio } from "lucide-react";

const TIER_COPY = {
  degoogled: {
    id: "01",
    name: "Degoogled",
    color: "#888888",
    Icon: ShieldOff,
    subtitle: "The first step out of the surveillance economy.",
    body: "Google Play Services excised. F-Droid, hardened DNS, and Signal pre-loaded. The clean baseline.",
  },
  secure: {
    id: "02",
    name: "Secure",
    color: "#EAEAEA",
    Icon: Lock,
    subtitle: "Hardened kernel. Routed traffic. Nothing leaks.",
    body: "Hardened memory allocator, always-on Mullvad VPN with kill-switch, sensors sandboxed, duress PIN.",
  },
  phantom: {
    id: "03",
    name: "Phantom",
    color: "#7C3AED",
    Icon: Radio,
    subtitle: "Microphones removed. Silence weaponised.",
    body: "Physical mic disconnect, USB data pins severed, hardware attestation, custom bootloader, Faraday case.",
  },
};

function ProductCard({ p, idx }) {
  return (
    <FadeUp delay={idx * 0.06}>
      <Link to={`/product/${p.slug}`} data-testid={`product-card-${p.slug}`} className="group block card-tech">
        <div className="relative aspect-[4/5] overflow-hidden bg-[#080808]">
          <motion.img
            src={p.image_url}
            alt={p.name}
            className="absolute inset-0 w-full h-full object-cover img-clip"
            initial={{ scale: 1.08 }}
            whileHover={{ scale: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-transparent" />
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="tier-dot degoogled" />
            <span className="tier-dot secure" />
            <span className="tier-dot phantom" />
            <span className="overline">3 tiers</span>
          </div>
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
            <ArrowUpRight size={20} className="text-[#EAEAEA]" />
          </div>
        </div>
        <div className="p-6 flex items-start justify-between gap-4">
          <div>
            <div className="font-display text-2xl tracking-tight">{p.name}</div>
            <div className="text-[#888] text-xs mt-1 uppercase tracking-widest">{p.model}</div>
          </div>
          <div className="text-right">
            <div className="text-[#EAEAEA] font-mono text-lg">{fmtAUD(p.base_price_aud || p.price_aud)}</div>
            <div className="overline mt-1">from</div>
          </div>
        </div>
      </Link>
    </FadeUp>
  );
}

function Tiers() {
  return (
    <section className="px-6 md:px-10 py-24 md:py-40" data-testid="tiers-section">
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16">
        <div className="col-span-12 md:col-span-3">
          <div className="overline">Ξ 002 — The Tiers</div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">
            <RevealLineScroll i={0}>Three levels of</RevealLineScroll>
            <RevealLineScroll i={1}>silence. Choose</RevealLineScroll>
            <RevealLineScroll i={2}>your threat model.</RevealLineScroll>
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-0 border border-[#1A1A1A]">
        {Object.entries(TIER_COPY).map(([k, t], idx) => {
          const Icon = t.Icon;
          const isLast = idx === 2;
          return (
            <FadeUp key={k} delay={idx * 0.1} className={`col-span-12 md:col-span-4 border-[#1A1A1A] ${idx < 2 ? "md:border-r" : ""} border-b md:border-b-0 last:border-b-0`}>
              <div className="p-8 md:p-10 h-full flex flex-col justify-between min-h-[420px] group hover:bg-[#080808] transition-colors">
                <div>
                  <div className="flex items-center justify-between">
                    <div className="overline">TIER {t.id}</div>
                    <span className={`tier-dot ${k}`} />
                  </div>
                  <div className="mt-8">
                    <Icon size={32} style={{ color: t.color }} strokeWidth={1.2} />
                  </div>
                  <div className="font-display text-5xl md:text-6xl mt-6 uppercase tracking-tight" style={{ color: t.color }}>
                    {t.name}
                  </div>
                  <div className="mt-4 text-[#EAEAEA] text-sm">{t.subtitle}</div>
                  <p className="mt-6 text-[#888] text-sm leading-relaxed">{t.body}</p>
                </div>
                <Link
                  to="/shop"
                  data-testid={`tier-cta-${k}`}
                  className="mt-12 overline flex items-center gap-2 text-[#EAEAEA] group-hover:text-white transition-colors"
                >
                  Browse devices <ArrowUpRight size={14} />
                </Link>
              </div>
            </FadeUp>
          );
        })}
      </div>
    </section>
  );
}

function Featured({ products }) {
  return (
    <section className="px-6 md:px-10 py-24" data-testid="featured-section">
      <div className="flex items-end justify-between mb-16 gap-6">
        <div>
          <div className="overline mb-6">Ξ 003 — The Catalogue</div>
          <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">
            <RevealLineScroll i={0}>Seven devices.</RevealLineScroll>
            <RevealLineScroll i={1}>One doctrine.</RevealLineScroll>
          </h2>
        </div>
        <Link to="/shop" className="btn-ghost hidden md:inline-flex">View all →</Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {products.slice(0, 6).map((p, i) => (
          <ProductCard key={p.id} p={p} idx={i} />
        ))}
      </div>
      <div className="mt-12 md:hidden">
        <Link to="/shop" className="btn-vault w-full justify-center">View full catalogue</Link>
      </div>
    </section>
  );
}

function Manifesto() {
  const chapters = [
    {
      n: "01",
      title: "Sovereignty is not a feature.",
      body: "Every OS you did not build is negotiating with itself about how much of you to expose. GrapheneOS refuses. We refuse harder.",
      img: "https://images.pexels.com/photos/17887828/pexels-photo-17887828.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
      n: "02",
      title: "The vault stays shut.",
      body: "Verified boot, hardware attestation, encrypted at rest, encrypted in transit. Zero-knowledge from bootloader to fingertip.",
      img: "https://images.unsplash.com/photo-1709417905029-d46be72e5115?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHNlY3VyZSUyMHZhdWx0JTIwZGFya3xlbnwwfHx8fDE3ODQ1OTE2NTd8MA&ixlib=rb-4.1.0&q=85",
    },
    {
      n: "03",
      title: "Made in Australia. For anyone anywhere.",
      body: "We hand-harden every device in Sydney. Chain of custody documented. Supply chain audited. Owned outright by Australians.",
      img: "https://images.pexels.com/photos/14866072/pexels-photo-14866072.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
  ];
  return (
    <section className="px-6 md:px-10 py-24 md:py-40" data-testid="manifesto-section">
      <div className="overline mb-6">Ξ 004 — Manifesto</div>
      <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.95] mb-24">
        <RevealLineScroll i={0}>What we believe.</RevealLineScroll>
        <RevealLineScroll i={1}>Why it matters.</RevealLineScroll>
      </h2>
      <div className="space-y-32">
        {chapters.map((c, i) => (
          <FadeUp key={c.n}>
            <div className={`grid grid-cols-12 gap-6 md:gap-10 items-center ${i % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""}`}>
              <div className="col-span-12 md:col-span-6">
                <div className="relative overflow-hidden aspect-[4/5]">
                  <img src={c.img} alt="" className="absolute inset-0 w-full h-full object-cover img-clip" />
                  <div className="absolute inset-0 border border-[#1A1A1A]" />
                </div>
              </div>
              <div className="col-span-12 md:col-span-6">
                <div className="font-display text-[7rem] md:text-[9rem] leading-none text-[#222]">{c.n}.</div>
                <h3 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.95] mt-4">
                  {c.title}
                </h3>
                <p className="mt-8 text-[#888] text-base leading-relaxed max-w-lg">{c.body}</p>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

function ContactCTA() {
  return (
    <section className="px-6 md:px-10 py-32 border-t border-[#1A1A1A]" data-testid="contact-cta">
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-6">
          <div className="overline mb-6">Ξ 005 — Transmission</div>
          <h2 className="font-display uppercase text-5xl md:text-7xl tracking-tight leading-[0.9]">
            <RevealLineScroll i={0}>Have a</RevealLineScroll>
            <RevealLineScroll i={1}>threat model?</RevealLineScroll>
          </h2>
        </div>
        <div className="col-span-12 md:col-span-6 flex flex-col justify-end">
          <p className="text-[#888] max-w-md text-base leading-relaxed">
            Bulk orders, custom hardening, corporate fleets, or one very specific set of paranoid requirements —
            open an encrypted line.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn-vault" data-testid="contact-cta-link">
              Open transmission <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);
  return (
    <div>
      <HeroVault />
      <EditorialMarquee />
      <Tiers />
      <Featured products={products} />
      <Manifesto />
      <ContactCTA />
    </div>
  );
}
